import { ChangeDetectionStrategy, Component, computed, inject, input, output } from '@angular/core';
import { TuiTabs } from '@taiga-ui/kit';
import { PermissionManagementService } from '@core/services/permission-management.service';
import { UserListComponent } from './components/user-list/user-list';
import { RoleListComponent } from './components/role-list/role-list';

export type UsersRolesTab = 'users' | 'roles';

/** The right that shows each tab; the section shows while at least one tab does. */
export const USERS_ROLES_TAB_RIGHTS: Record<UsersRolesTab, string> = {
  users: 'admin_users_list',
  roles: 'admin_rights_list',
};

const USERS_ROLES_TABS: readonly { id: UsersRolesTab; label: string }[] = [
  { id: 'users', label: 'Users' },
  { id: 'roles', label: 'Roles and permissions' },
];

@Component({
  selector: 'app-settings-users-roles',
  templateUrl: 'users-roles.html',
  styleUrl: 'users-roles.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [TuiTabs, UserListComponent, RoleListComponent],
})
export class UsersRolesComponent {
  private readonly permissions = inject(PermissionManagementService);

  readonly activeTab = input<UsersRolesTab>('users');

  readonly tabChange = output<UsersRolesTab>();
  readonly addUser = output<void>();
  readonly editUser = output<number>();
  readonly addRole = output<void>();
  readonly editRole = output<number>();
  readonly copyRole = output<number>();

  protected readonly tabs = computed(() =>
    USERS_ROLES_TABS.filter((tab) => this.permissions.can(USERS_ROLES_TAB_RIGHTS[tab.id])),
  );

  protected readonly activeTabIndex = computed(() =>
    this.tabs().findIndex((tab) => tab.id === this.activeTab()),
  );

  protected selectTab(index: number): void {
    const tab = this.tabs()[index];
    if (tab) {
      this.tabChange.emit(tab.id);
    }
  }
}
